import React, { useRef } from 'react';
import {
    Box,
    Button,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import axios from 'axios';
import html2pdf from "html2pdf.js";
import PMLogo from '../data/pmlogo-removebg-preview.png'; // Update path if needed

const InvoicePreview = ({ invoiceData, setShowPreview, resetForm }) => {
    const componentRef = useRef();

    const handleDownloadPDF = () => {
        const element = componentRef.current;
        const opt = {
            margin: 0.5,
            filename: `invoice-${invoiceData.invoiceNo}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
        };

        html2pdf().set(opt).from(element).save().then(() => {
            setShowPreview(false);
            resetForm()
        });
    };


    const handleCreateInvoice = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await axios.post(
                `${process.env.REACT_APP_MY_API}/api/invoices/create`,
                invoiceData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (res.data.success) {
                    handleDownloadPDF();     
            }
        } catch (err) {
            console.error("Failed to create invoice", err);
        }
    };

    return (
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, overflowX: 'auto' }}>
            <Box ref={componentRef} sx={{ color: 'black', fontSize: '14px' }}>
                {/* Header */}
                <Grid container spacing={2} justifyContent="space-between" alignItems="center">
                    <Grid item xs={12} sm={8}>
                        <Typography variant="h5" sx={{ fontFamily: 'monospace' }}>
                            Tax Invoice
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} textAlign={{ xs: 'left', sm: 'right' }}>
                        <Box
                            component="img"
                            src={PMLogo}
                            alt="Logo"
                            sx={{
                                width: { xs: 60, sm: 80 },
                                height: { xs: 60, sm: 80 },
                                objectFit: 'contain',
                                borderRadius: '50%',
                            }}
                        />
                    </Grid>
                </Grid>

                {/* From / Invoice Details */}
                <Grid container spacing={2} mt={2} justifyContent={'space-between'}>
                    <Grid item xs={12} sm={6}>
                        <Typography fontWeight="bold">FROM</Typography>
                        <Typography>P.M ENTERPRISE</Typography>
                        <Typography>4/B, Saraswatinagar, Gayatinagar</Typography>
                        <Typography>Chandlodia, Ahmedabad, Gujarat - 382481</Typography>
                        <Typography>PAN: ABOPY8217G</Typography>
                        <Typography>GSTIN: 24ABOPY8217G2Z5</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} textAlign={{ xs: 'left', sm: 'right' }}>
                        <Typography><strong>INVOICE:</strong> {invoiceData.invoiceNo || 'Generating...'}</Typography>
                        <Typography><strong>INVOICE DATE:</strong> {invoiceData.invoiceDate}</Typography>
                        <Typography><strong>DUE DATE:</strong> {invoiceData.dueDate || '---'}</Typography>
                    </Grid>
                </Grid>

                {/* Bill To */}
                <Grid container spacing={2} mt={4}>
                    <Grid item xs={12}>
                        <Typography fontWeight="bold">BILL TO</Typography>
                        <Typography>{invoiceData.customerName}</Typography>
                        <Typography>{invoiceData.address}</Typography>
                        {invoiceData?.gstin && (
                            <Typography>GSTIN: {invoiceData.gstin}</Typography>
                        )}
                        <Typography>{invoiceData.customerNumber}</Typography>

                    </Grid>
                </Grid>

                {/* Table */}
                <Box sx={{ mt: 4, overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Description</TableCell>
                                <TableCell>HSN/Code</TableCell>
                                <TableCell>QTY</TableCell>
                                <TableCell align="right">Unit Price</TableCell>
                                <TableCell align="center">Amount</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invoiceData.items.map((item, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>{item.product}</TableCell>
                                    <TableCell>{item.hsn}</TableCell>
                                    <TableCell>{item.quantity}</TableCell>
                                    <TableCell align="right">₹{item.unitPrice}</TableCell>
                                    <TableCell align="center">₹{item.total}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>

                {/* Summary */}
                <Grid container justifyContent="flex-end" mt={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        <Typography textAlign="right">Subtotal: ₹{invoiceData.grossAmount - invoiceData.cgst - invoiceData.sgst}</Typography>
                        <Typography textAlign="right">CGST 9%: ₹{invoiceData.cgst}</Typography>
                        <Typography textAlign="right">SGST 9%: ₹{invoiceData.sgst}</Typography>
                        <Typography variant="h6" textAlign="right" fontWeight="bold" mt={1}>
                            TOTAL: ₹{invoiceData.grossAmount}
                        </Typography>
                    </Grid>
                </Grid>

                {/* Bank Details + Signature */}
                <Grid container spacing={2} mt={4} justifyContent={'space-between'}>
                    {/* Bank Details */}
                    <Grid item xs={12} md={6}>
                        <Typography fontWeight="bold" gutterBottom>
                            BANK DETAILS
                        </Typography>
                        <Typography><strong>Bank Name:</strong> Bank of India</Typography>
                        <Typography><strong>Account Number:</strong> 202920110000237</Typography>
                        <Typography><strong>IFSC Code:</strong> BKID0002029</Typography>
                        <Typography><strong>Branch:</strong> Chandlodia, Ahmedabad</Typography>
                    </Grid>

                    {/* Signature */}
                    <Grid item xs={12} md={6} textAlign={{ xs: 'left', md: 'right' }}>
                        <Typography fontWeight="bold" gutterBottom>
                            Authorized Signature
                        </Typography>
                        <img
                            src="/assets/pmstamped.png"
                            alt="Signature"
                            style={{ width: 120, height: 'auto', opacity: 0.6 }}
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Print Button */}
            <Box mt={4} textAlign="right">
                <Button variant="contained" color="primary" onClick={handleCreateInvoice}>
                    Download Invoice
                </Button>
            </Box>
        </Paper>
    );
};

export default InvoicePreview;
